import { IShopRecord } from "@src/types/shopRecord";
import { IVinyl } from "@src/types/vinyl";

// Helper function to normalize vinyl data from different sources
export const normalizeVinylData = (
  record: IShopRecord | IVinyl,
  isFromShopRecord: boolean
): Partial<IVinyl> | null => {
  const recordId = record?.id;
  if (
    recordId === null ||
    recordId === undefined ||
    (typeof recordId === "string" && recordId === "")
  ) {
    return null;
  }

  // Determine the correct image URL field
  const imageUrl = isFromShopRecord
    ? (record as IShopRecord).imageUrl
    : (record as IVinyl).x_studio_discogs_image_link;

  return {
    id: record.id || undefined,
    year: record.year || undefined,
    label: record.label || undefined,
    artist: record.artist || undefined,
    name: record.name || undefined,
    description_demo: isFromShopRecord
      ? (record as IShopRecord).description || undefined
      : (record as IVinyl).description_demo || undefined,
    x_studio_discogs_image_link: imageUrl || undefined,
    product_tmpl_id: (record as IVinyl).product_tmpl_id,
    qty_available: (record as IVinyl).qty_available,
    // A saved row's `id` may be Strapi's own PK, so only its `odooId` counts.
    ...(isFromShopRecord
      ? { odooId: (record as IShopRecord).odooId ?? null }
      : {}),
  };
};

/**
 * The Odoo id to submit for a pick [ROVR-4188]: a restored pick's `odooId`
 * (possibly null), otherwise the catalogue entry's own `id`.
 */
export const getOdooId = (vinyl?: Partial<IVinyl> | null): number | null => {
  if (vinyl?.odooId !== undefined) return vinyl.odooId;
  return vinyl?.id ? Number(vinyl.id) : null;
};

/**
 * Rebuilds a saved pick for its slot, from the shop record and the live
 * selected-vinyls catalogue [ROVR-4188].
 */
export const resolveSavedPick = (
  record: IShopRecord,
  catalogue?: IVinyl[] | null
): Partial<IVinyl> | null => {
  // What the curator picked, as stored at submit time. Only this copy has
  // a cover that survives the vinyl being sold: the Odoo catalogue filters
  // on qty_available, so a sold pick drops out of it entirely.
  const stored = normalizeVinylData(record, true);
  // Keyed like a catalogue entry, so the grid marks the pick as added and
  // `refreshSavedPick` can still match it once the catalogue lands. Only a
  // lookup key: what gets submitted as the Odoo id is `odooId`.
  if (stored) stored.id = record.odooId ?? record.id;

  return withLiveEntry(stored, catalogue);
};

/**
 * A restored pick merged with its live catalogue entry, when there is one.
 * The live catalogue wins for the rest of the fields — product_tmpl_id and
 * qty_available are not stored on the shop record and handleSubmit needs them
 * to re-submit the selection.
 */
const withLiveEntry = (
  stored: Partial<IVinyl> | null,
  catalogue?: IVinyl[] | null
): Partial<IVinyl> | null => {
  const found = stored?.id
    ? catalogue?.find((v) => v.id === stored.id)
    : undefined;
  if (!found) {
    return stored;
  }

  const live = normalizeVinylData(found, false);
  if (live?.x_studio_discogs_image_link) {
    return live;
  }
  // Live entry has no cover — keep its richer fields, take the stored one.
  return {
    ...live,
    x_studio_discogs_image_link: stored?.x_studio_discogs_image_link,
  };
};

/**
 * Re-resolves a restored pick against a catalogue that landed after the saved
 * selection did. Without it, a pick saved before ROVR-4188 (no stored cover)
 * stayed on the placeholder for good.
 *
 * Only a restored pick that wasn't matched yet is touched — it is the one that
 * still carries `odooId` (possibly null). Picks from the grid, and restored
 * picks already merged with their live entry, come back as they are.
 */
export const refreshSavedPick = (
  pick: Partial<IVinyl> | null,
  catalogue?: IVinyl[] | null
): Partial<IVinyl> | null =>
  pick && pick.odooId !== undefined ? withLiveEntry(pick, catalogue) : pick;
